import { Lock } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { DOMAIN_PHYSICAL, DOMAIN_SHOOTING } from "../../lib/domainAccess";
import { slugifySport } from "../../lib/utils";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const FAMILIES = [
	{ value: DOMAIN_PHYSICAL, labelKey: "sdfFamilyPhysical" },
	{ value: DOMAIN_SHOOTING, labelKey: "sdfFamilyShooting" },
];

/** Il form vuoto, per chi crea. */
export const EMPTY_SPORT_FORM = {
	label: "",
	value: "",
	families: [DOMAIN_PHYSICAL],
	order: 0,
};

/** Il form di uno sport esistente, per chi modifica. */
export function sportToForm(sport) {
	return {
		label: sport?.label || "",
		value: sport?.value || "",
		// Le righe storiche senza `families` il backend le legge come
		// preparazione fisica: meglio mostrarlo che lasciare un vuoto ambiguo.
		families:
			Array.isArray(sport?.families) && sport.families.length > 0
				? sport.families
				: [DOMAIN_PHYSICAL],
		order: sport?.order ?? 0,
	};
}

/**
 * Il form di uno sport, **uno solo** per l'admin e per il coach
 * (docs/sport-config, Fase 3).
 *
 *  - il coach scrive solo il nome: lo slug lo ricava il server, e il suo sport
 *    nasce di preparazione fisica (le discipline di tiro le crea l'admin, D11);
 *  - l'admin vede anche identificativo, ambiti e ordine.
 *
 * L'identificativo si sceglie **solo creando**: modificando si vede bloccato,
 * perché ci puntano atleti, coach e modelli (D5). Finché l'admin non lo tocca,
 * segue il nome.
 *
 * Props: value, onChange(next), mode ("create" | "edit"),
 *        variant ("admin" | "coach"), disabled.
 */
export function SportDisciplineForm({
	value = EMPTY_SPORT_FORM,
	onChange,
	mode = "create",
	variant = "coach",
	disabled = false,
}) {
	const { t } = useLanguage();
	const isAdmin = variant === "admin";
	const creating = mode !== "edit";
	const families = Array.isArray(value.families) ? value.families : [];

	const update = (patch) => onChange?.({ ...value, ...patch });

	const changeLabel = (label) => {
		// Lo slug segue il nome finché coincide con quello del nome precedente:
		// se l'admin l'ha scritto a mano, non glielo si cambia sotto il naso.
		const follows = !value.value || value.value === slugifySport(value.label);
		update({
			label,
			...(creating && follows ? { value: slugifySport(label) } : {}),
		});
	};

	const toggleFamily = (family) =>
		update({
			families: families.includes(family)
				? families.filter((item) => item !== family)
				: [...families, family],
		});

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<Label htmlFor="sport-label">{t("sdfLabel")}</Label>
				<Input
					id="sport-label"
					value={value.label}
					disabled={disabled}
					maxLength={60}
					onChange={(event) => changeLabel(event.target.value)}
					className="bg-secondary border-transparent rounded-sm"
					placeholder={t("sdfLabelPlaceholder")}
				/>
				{!isAdmin && (
					<p className="text-xs text-muted-foreground">{t("sdfCoachHint")}</p>
				)}
			</div>

			{isAdmin && (
				<div className="space-y-2">
					<Label htmlFor="sport-value">{t("sdfSlug")}</Label>
					{creating ? (
						<Input
							id="sport-value"
							value={value.value}
							disabled={disabled}
							onChange={(event) => update({ value: slugifySport(event.target.value) })}
							className="bg-secondary border-transparent rounded-sm font-mono text-sm"
							placeholder="crossfit"
						/>
					) : (
						<div className="flex items-center gap-2 rounded-sm bg-secondary/60 px-3 py-2 font-mono text-sm text-muted-foreground">
							<Lock className="w-3.5 h-3.5 shrink-0" />
							<span className="truncate">{value.value}</span>
						</div>
					)}
					<p className="text-xs text-muted-foreground">
						{creating ? t("sdfSlugHint") : t("sdfSlugLocked")}
					</p>
				</div>
			)}

			{isAdmin && (
				<div className="space-y-2">
					<Label>{t("sdfFamilies")}</Label>
					<p className="text-xs text-muted-foreground">{t("sdfFamiliesHint")}</p>
					<div className="flex flex-wrap gap-2">
						{FAMILIES.map((family) => {
							const selected = families.includes(family.value);
							return (
								<button
									key={family.value}
									type="button"
									aria-pressed={selected}
									disabled={disabled}
									onClick={() => toggleFamily(family.value)}
									className={`rounded-sm border px-3 py-1.5 text-sm transition-colors disabled:opacity-50 ${
										selected
											? "border-volt/60 bg-volt/15 text-volt"
											: "border-border text-muted-foreground hover:border-volt/40"
									}`}
								>
									{t(family.labelKey)}
								</button>
							);
						})}
					</div>
					{families.length === 0 && (
						<p className="text-xs text-amber-400">{t("sdfNoFamilies")}</p>
					)}
				</div>
			)}

			{isAdmin && (
				<div className="space-y-2 max-w-[10rem]">
					<Label htmlFor="sport-order">{t("sdfOrder")}</Label>
					<Input
						id="sport-order"
						type="number"
						value={value.order}
						disabled={disabled}
						onChange={(event) => update({ order: event.target.value })}
						className="bg-secondary border-transparent rounded-sm"
					/>
				</div>
			)}
		</div>
	);
}
